import React, { useCallback, useRef } from 'react';
import moment from 'moment';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { getOpmData } from 'pages/Valuations/approaches/backsolveApproach/BacksolveApproach';
import {
  MATURITY_ALIAS,
  OPM_BACKSOLVE_DATE_ALIAS,
} from 'pages/Valuations/approaches/backsolveApproach/OPMInputTable/constants';
import { useGetRiskFreeRate } from 'services/hooks/allocation';
import { OPMInputTableProps } from './types';

export const getRiskFreeRateKey = (maturity: unknown, backsolveDate: unknown) => `${maturity}|${backsolveDate}`;

const OPMInputTable = ({
  spreadsheets,
  onChange,
  setPrevOpmData,
  isLedgerTable,
  componentId,
}: OPMInputTableProps) => {
  const { opmInputSheet } = spreadsheets;
  const [getRiskFreeRateByDate] = useGetRiskFreeRate();
  const backsolveDate = opmInputSheet?.cells?.OPM_BACKSOLVE_DATE?.value;
  const maturityValue = opmInputSheet?.cells?.MATURITY?.value;

  // The workbook re-evaluates (and replaces every cell object) while the rate fetch is
  // in flight. Writing the response into the cell captured before that swap lands on an
  // orphan, so the write always goes through the latest cell and onChange instead.
  const latestRiskFreeRateCellRef = useRef(opmInputSheet?.cells?.RISK_FREE_RATE);
  const latestOnChangeRef = useRef(onChange);
  latestRiskFreeRateCellRef.current = opmInputSheet?.cells?.RISK_FREE_RATE;
  latestOnChangeRef.current = onChange;

  const inFlightRiskFreeRateKeysRef = useRef(new Set<string>());
  const lastRequestedRiskFreeRateKeyRef = useRef<string | null>(null);

  const changeRiskFreeRate = useCallback(
    async (maturity, queryDate) => {
      if (!Number.isFinite(Number(maturity)) || Number(maturity) <= 0 || !moment(queryDate, 'MM/DD/YYYY', true).isValid()) {
        // An invalid edit must also invalidate an earlier request and its stored rate.
        lastRequestedRiskFreeRateKeyRef.current = null;
        if (latestRiskFreeRateCellRef.current) {
          latestOnChangeRef.current(latestRiskFreeRateCellRef.current, '', true);
        }
        return;
      }
      const riskFreeRateKey = getRiskFreeRateKey(maturity, queryDate);
      lastRequestedRiskFreeRateKeyRef.current = riskFreeRateKey;
      if (inFlightRiskFreeRateKeysRef.current.has(riskFreeRateKey)) {
        return;
      }
      inFlightRiskFreeRateKeysRef.current.add(riskFreeRateKey);
      try {
        const rfr = (await getRiskFreeRateByDate(
          maturity,
          moment(queryDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
        )) as number | undefined;
        if (
          lastRequestedRiskFreeRateKeyRef.current === riskFreeRateKey
          && latestRiskFreeRateCellRef.current
        ) {
          if (typeof rfr === 'number' && Number.isFinite(rfr)) {
            latestOnChangeRef.current(latestRiskFreeRateCellRef.current, Number(rfr.toFixed(6)));
          } else {
            // The hook reports lookup failures. Do not reuse the previous input's rate.
            latestOnChangeRef.current(latestRiskFreeRateCellRef.current, '', true);
          }
        }
      } finally {
        inFlightRiskFreeRateKeysRef.current.delete(riskFreeRateKey);
      }
    },
    [getRiskFreeRateByDate]
  );

  const onWorkbookChange = useCallback(
    (cell, value) => {
      const opmData = getOpmData(spreadsheets);
      if (setPrevOpmData) {
        setPrevOpmData(opmData.map(cell => ({ ...cell })));
      }
      // The persisted rate still belongs to the previous maturity/date until
      // the lookup finishes. Its write below triggers the solve with both new inputs.
      const requiresRateLookup = cell.alias === MATURITY_ALIAS || cell.alias === OPM_BACKSOLVE_DATE_ALIAS;
      const pendingRateKey = lastRequestedRiskFreeRateKeyRef.current;
      const isRateLookupPending = pendingRateKey !== null && inFlightRiskFreeRateKeysRef.current.has(pendingRateKey);
      if (requiresRateLookup || isRateLookupPending) {
        onChange(cell, value, true);
      } else {
        onChange(cell, value);
      }
      if (cell.alias === MATURITY_ALIAS) {
        changeRiskFreeRate(value, backsolveDate);
      }
      if (cell.alias === OPM_BACKSOLVE_DATE_ALIAS) {
        changeRiskFreeRate(maturityValue, value);
      }
    },
    [spreadsheets, setPrevOpmData, onChange, changeRiskFreeRate, backsolveDate, maturityValue]
  );

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore: ScalarSpreadsheet is not typed
    <ScalarSpreadsheet
      {...opmInputSheet}
      id={componentId ?? 'opm-input-table'}
      tableTerms={{ tableSlug: 'opm-input-table' }}
      onChange={onWorkbookChange}
      sheet={opmInputSheet}
      displayLegend={false}
      showToolbar
      alwaysDisplayLegend={false}
      isLedgerTable={isLedgerTable}
    />
  );
};

export default OPMInputTable;
